import type {
	AnalyticsPeriod,
	AnalyticsRange,
	AnalyticsSelection,
} from '../types';

export const DEFAULT_ANALYTICS_PERIOD: AnalyticsPeriod = '30d';

/** How far back each period reaches, today included. */
const PERIOD_SPANS: Record<
	AnalyticsPeriod,
	{ readonly days: number } | { readonly months: number }
> = {
	'30d': { days: 30 },
	'60d': { days: 60 },
	'90d': { days: 90 },
	'6m': { months: 6 },
	'1y': { months: 12 },
};

const INDIA_DATE = new Intl.DateTimeFormat('en-CA', {
	year: 'numeric',
	month: '2-digit',
	day: '2-digit',
	timeZone: 'Asia/Kolkata',
});

/** Today's date in India, as yyyy-MM-dd: the API's periods are IST dates. */
export function todayInIndia(now: Date = new Date()): string {
	return INDIA_DATE.format(now);
}

/** A calendar date as midnight UTC, so day and month steps ignore time zones. */
function parseDate(date: string): Date {
	const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
	return new Date(Date.UTC(year, month - 1, day));
}

function formatDate(date: Date): string {
	return date.toISOString().slice(0, 10);
}

/** `months` back from `date`, kept to the last day of a shorter month
 *  (31 Aug − 6 months → 28 Feb). */
function monthsBefore(date: Date, months: number): Date {
	const year = date.getUTCFullYear();
	const month = date.getUTCMonth() - months;
	const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
	return new Date(Date.UTC(year, month, Math.min(date.getUTCDate(), lastDay)));
}

/**
 * The days a period covers, ending today and both inclusive: "Last 30 days"
 * is today and the 29 days before it, the API's own default. A month-based
 * period starts the day after the same date that many months back, so a year
 * never exceeds the API's 366-day limit.
 */
export function analyticsRange(
	period: AnalyticsPeriod,
	today: string = todayInIndia(),
): AnalyticsRange {
	const to = parseDate(today);
	const span = PERIOD_SPANS[period];
	const from =
		'days' in span ? new Date(to.getTime()) : monthsBefore(to, span.months);
	from.setUTCDate(from.getUTCDate() + ('days' in span ? 1 - span.days : 1));
	return { from: formatDate(from), to: today };
}

/** The API's longest period, both ends counted. */
const MAX_PERIOD_DAYS = 366;
const DAY_MS = 86_400_000;

/**
 * The days a custom range can use: today and the 365 days before it, so any
 * range inside them is within the API's 366-day limit.
 */
export function customRangeLimits(
	today: string = todayInIndia(),
): AnalyticsRange {
	const from = parseDate(today);
	from.setUTCDate(from.getUTCDate() - (MAX_PERIOD_DAYS - 1));
	return { from: formatDate(from), to: today };
}

function isDate(value: string): boolean {
	return (
		/^\d{4}-\d{2}-\d{2}$/.test(value) && formatDate(parseDate(value)) === value
	);
}

/** Real dates, in order, inside the custom range limits: what the calendar
 *  can pick, checked again for a range that comes from the URL. */
export function isAllowedRange(
	range: AnalyticsRange,
	today: string = todayInIndia(),
): boolean {
	const limits = customRangeLimits(today);
	return (
		isDate(range.from) &&
		isDate(range.to) &&
		limits.from <= range.from &&
		range.from <= range.to &&
		range.to <= limits.to
	);
}

/** The days a selection covers: a preset's, ending today, or the dates
 *  picked. */
export function selectionRange(
	selection: AnalyticsSelection,
	today: string = todayInIndia(),
): AnalyticsRange {
	return selection.period === 'custom'
		? selection.range
		: analyticsRange(selection.period, today);
}

/** Days in a range, both ends counted. */
export function rangeDays(range: AnalyticsRange): number {
	return (
		(parseDate(range.to).getTime() - parseDate(range.from).getTime()) / DAY_MS +
		1
	);
}
